import { useMemo, useState } from "react";
import { Building2, Pencil, Plus, Trash2, Users } from "lucide-react";
import api from "../../api";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../utils/apiError";
import { ApiResponse, User } from "../../types";
import { ALL_ROLES, USER_STATUSES } from "../../constants/enums";
import { formatDate, initials } from "../../utils/format";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button, { IconButton } from "../../components/ui/Button";
import DataTable, { Column, RowActions } from "../../components/ui/DataTable";
import { FilterSelect, SearchInput, Toolbar } from "../../components/ui/Toolbar";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Badge, { StatusBadge } from "../../components/ui/Badge";
import Switch from "../../components/ui/Switch";
import UserFormModal from "./UserFormModal";

export default function UsersPage() {
    const { user: me } = useAuth();
    const toast = useToast();

    const [role, setRole] = useState("");
    const [status, setStatus] = useState("");
    const [search, setSearch] = useState("");

    const { data, loading, error, reload, setData } = useApi<User[]>("/users", { role, status });

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<User | null>(null);
    const [deleting, setDeleting] = useState<User | null>(null);
    const [toggling, setToggling] = useState<number | null>(null);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!data) return null;
        if (!q) return data;
        return data.filter((u) => [u.name, u.username, u.email, u.phone].filter(Boolean).some((v) => v!.toLowerCase().includes(q)));
    }, [data, search]);

    const counts = useMemo(() => {
        const list = data ?? [];
        return { total: list.length, inactive: list.filter((u) => !u.isActive).length };
    }, [data]);

    const toggleActive = async (u: User, isActive: boolean) => {
        setToggling(u.id);
        try {
            const res = await api.patch<ApiResponse<User>>(`/users/${u.id}/status`, { isActive });
            // Update the row in place (or drop it if it no longer matches the status filter)
            setData((list) =>
                (list ?? [])
                    .map((x) => (x.id === u.id ? { ...x, ...res.data.data } : x))
                    .filter((x) => !status || (status === "active") === !!x.isActive)
            );
            toast.success(isActive ? `${u.name} activated` : `${u.name} deactivated and signed out`);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setToggling(null);
        }
    };

    const handleDelete = async () => {
        if (!deleting) return;
        try {
            await api.delete(`/users/${deleting.id}`);
            toast.success("User deleted");
            setDeleting(null);
            reload();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const openCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const columns: Column<User>[] = [
        {
            key: "user",
            header: "User",
            render: (u) => (
                <div className="flex items-center gap-3 min-w-0">
                    <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 ${
                            u.isActive
                                ? "bg-gradient-to-br from-[#8b5cf6] to-[#6d28d9] text-white"
                                : "bg-slate-800 text-slate-500 border border-slate-700"
                        }`}
                    >
                        {initials(u.name)}
                    </div>
                    <div className="min-w-0">
                        <p className={`font-semibold truncate ${u.isActive ? "text-white" : "text-slate-400"}`}>
                            {u.name}
                            {u.id === me?.id && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#a78bfa]">You</span>}
                        </p>
                        <p className="text-xs text-slate-500 truncate">@{u.username}</p>
                    </div>
                </div>
            ),
        },
        {
            key: "contact",
            header: "Contact",
            hideOnMobile: true,
            render: (u) => (
                <div className="text-xs min-w-0">
                    <p className="text-slate-300 truncate">{u.email}</p>
                    <p className="text-slate-500">{u.phone || "—"}</p>
                </div>
            ),
        },
        { key: "role", header: "Role", render: (u) => <StatusBadge value={u.role} /> },
        {
            key: "linked",
            header: "Linked to",
            hideOnMobile: true,
            render: (u) =>
                u.customer ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                        <Building2 size={13} className="text-[#8b5cf6]" />
                        {u.customer.name}
                    </span>
                ) : u._count?.customers ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                        <Users size={13} className="text-[#8b5cf6]" />
                        {u._count.customers} customer{u._count.customers > 1 ? "s" : ""}
                    </span>
                ) : (
                    <span className="text-slate-600">—</span>
                ),
        },
        {
            key: "status",
            header: "Status",
            render: (u) => {
                const locked = u.role === "SUPERADMIN" || u.id === me?.id;
                return (
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={!!u.isActive}
                            loading={toggling === u.id}
                            disabled={locked}
                            onChange={(v) => toggleActive(u, v)}
                            label={locked ? "This account can't be deactivated" : u.isActive ? "Deactivate" : "Activate"}
                        />
                        <span className={`text-xs font-semibold hidden sm:inline ${u.isActive ? "text-emerald-400" : "text-slate-500"}`}>
                            {u.isActive ? "Active" : "Inactive"}
                        </span>
                    </div>
                );
            },
        },
        {
            key: "created",
            header: "Created",
            hideOnMobile: true,
            render: (u) => <span className="text-xs text-slate-500">{formatDate(u.createdAt)}</span>,
        },
        {
            key: "actions",
            header: "",
            className: "w-24 text-right",
            render: (u) => (
                <RowActions>
                    <IconButton
                        icon={Pencil}
                        label="Edit"
                        onClick={() => {
                            setEditing(u);
                            setFormOpen(true);
                        }}
                    />
                    {u.role !== "SUPERADMIN" && u.id !== me?.id && (
                        <IconButton
                            icon={Trash2}
                            tone="danger"
                            disabled={!!u._count?.customers}
                            className="disabled:opacity-30 disabled:pointer-events-none"
                            label={u._count?.customers ? `Owns ${u._count.customers} customer(s); reassign them first, or deactivate instead` : "Delete"}
                            onClick={() => setDeleting(u)}
                        />
                    )}
                </RowActions>
            ),
        },
    ];

    const filtered = !!(role || status || search);

    return (
        <>
            <PageHeader
                title="Users"
                subtitle={
                    data
                        ? `${counts.total} user${counts.total === 1 ? "" : "s"}${counts.inactive ? ` · ${counts.inactive} inactive` : ""}`
                        : "Manage who can sign in."
                }
                actions={
                    <Button icon={Plus} onClick={openCreate}>
                        Add user
                    </Button>
                }
            />

            <Card padded={false}>
                <Toolbar>
                    <SearchInput value={search} onChange={setSearch} placeholder="Name, username, email…" />
                    <FilterSelect value={role} onChange={setRole} options={ALL_ROLES} allLabel="All roles" />
                    <FilterSelect value={status} onChange={setStatus} options={USER_STATUSES} allLabel="Any status" />
                    {filtered && (
                        <button
                            onClick={() => {
                                setRole("");
                                setStatus("");
                                setSearch("");
                            }}
                            className="text-xs font-semibold text-[#a78bfa] hover:text-white px-2"
                        >
                            Clear
                        </button>
                    )}
                </Toolbar>
                <DataTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    error={error}
                    onRetry={reload}
                    rowKey={(u) => u.id}
                    emptyTitle={filtered ? "No matching users" : "No users yet"}
                    emptyText={filtered ? "Try changing the filters." : undefined}
                    emptyAction={
                        !filtered && (
                            <Button size="sm" icon={Plus} onClick={openCreate}>
                                Add user
                            </Button>
                        )
                    }
                />
            </Card>

            <UserFormModal
                open={formOpen}
                user={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => {
                    setFormOpen(false);
                    reload();
                }}
            />

            <ConfirmDialog
                open={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={handleDelete}
                title="Delete user?"
                message={
                    <>
                            <b className="text-white">{deleting?.name}</b> (@{deleting?.username}) will be removed and signed out
                            {deleting?.customer ? (
                                <>
                                    . Customer <b className="text-white">{deleting.customer.name}</b> stays, without a portal login
                                </>
                            ) : null}
                            . To only block sign-in temporarily, deactivate instead.
                        {" "}
                        <Badge tone="red">Can't be undone</Badge>
                    </>
                }
            />
        </>
    );
}